import { useState, useEffect, Fragment } from 'react'
import { Link, useLocation, Outlet, useNavigate, Navigate } from 'react-router-dom'
import { useTheme } from '@/contexts/ThemeContext'
import { useOrgStore } from '@/stores/orgStore'
import { useAuthStore } from '@/stores/authStore'
import { accountApi } from '@/api/clients/accountApi'
import { authApi } from '@/api/clients/authApi'
import { useEmbedStore } from '@/stores/embedStore'
import { isRouteAllowed } from '@/lib/embed'

interface NavItem {
  path: string
  icon: string
  label: string
}

const navItems: NavItem[] = [
  { path: '/', icon: 'dashboard', label: 'Dashboard' },
  { path: '/inventories', icon: 'inventory_2', label: 'Inventories' },
  { path: '/sources', icon: 'radar', label: 'Discovery Sources' },
  { path: '/scans', icon: 'manage_search', label: 'Scan Jobs' },
  { path: '/assets', icon: 'key', label: 'Crypto Assets' },
  { path: '/packages', icon: 'category', label: 'Packages' },
  { path: '/reports', icon: 'summarize', label: 'Reports' },
  { path: '/cmdb', icon: 'hub', label: 'CMDB' },
  { path: '/credentials', icon: 'vpn_key', label: 'Credentials' },
]

export default function Layout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const { theme, toggleTheme } = useTheme()
  const location = useLocation()
  const { currentOrgId, orgName } = useOrgStore()
  const { userId, userEmail, role, orgPlan, orgName: rawOrgName, setOrgInfo, setUserInfo, refreshToken, clearSession } = useAuthStore()
  const navigate = useNavigate()
  // Embed route allowlist from the partner parent (null = unrestricted).
  const allowedRoutes = useEmbedStore((s) => s.routes)
  const routeAllowed = isRouteAllowed(location.pathname, allowedRoutes)
  const visibleNavItems = navItems.filter((item) => isRouteAllowed(item.path, allowedRoutes))

  // Exposes the current route to partner CSS, e.g. [data-qtz-route^="/reports"] [data-qtz-part="breadcrumb"].
  useEffect(() => {
    document.documentElement.dataset.qtzRoute = location.pathname
  }, [location.pathname])

  const handleLogout = async () => {
    setShowUserMenu(false)
    if (refreshToken) {
      await authApi.logout(refreshToken).catch(() => {})
    }
    clearSession()
    navigate('/login', { replace: true })
  }

  useEffect(() => {
    if (!currentOrgId || rawOrgName !== null) return
    accountApi.getOrg(currentOrgId).then((org) => setOrgInfo(org.name, org.plan)).catch(() => {})
  }, [currentOrgId, rawOrgName, setOrgInfo])

  useEffect(() => {
    if (!currentOrgId || !userId || userEmail !== null) return
    accountApi.getUser(currentOrgId, userId).then((u) => setUserInfo(u.email, u.role)).catch(() => {})
  }, [currentOrgId, userId, userEmail, setUserInfo])

  const userInitials = userEmail ? userEmail.slice(0, 2).toUpperCase() : '?'
  const userRole = role ?? ''

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)

  // ── Breadcrumb ────────────────────────────────────────────────────────────────
  const ROUTE_LABELS: Record<string, string> = {
    sources: 'Discovery Sources',
    scans: 'Scan Jobs',
    assets: 'Crypto Assets',
    inventories: 'Inventories',
    packages: 'Packages',
    reports: 'Reports',
    cmdb: 'CMDB',
    credentials: 'Credentials',
    settings: 'Settings',
  }

  function Breadcrumb() {
    const parts = location.pathname.split('/').filter(Boolean)
    if (parts.length === 0) return null
    const segments = parts.map((p, i) => {
      if (i === 0) return ROUTE_LABELS[p] ?? p
      if (i === 1 && parts[0] !== 'settings') {
        return p.length > 8 ? p.slice(0, 8) + '…' : p
      }
      return ROUTE_LABELS[p] ?? p
    })
    return (
      <p data-qtz-part="breadcrumb" className="text-[11px] text-subtle-light dark:text-subtle-dark mt-0.5 leading-none">
        <span>Dashboard</span>
        {segments.map((s, i) => (
          <Fragment key={i}>
            <span className="mx-1 opacity-40">›</span>
            <span className={i === segments.length - 1 ? 'text-text-light dark:text-text-dark' : ''}>{s}</span>
          </Fragment>
        ))}
      </p>
    )
  }

  // ── Nav link — active state: solid square housing behind icon ────────────────
  // Light: icon bg = #254F71 (primary), icon = white
  // Dark:  icon bg = #94C8E4 (secondary), icon = #0F151B (neutral) for max contrast
  function NavLink({ item, onClick }: { item: NavItem; onClick?: () => void }) {
    const active = isActive(item.path)
    return (
      <Link
        to={item.path}
        onClick={onClick}
        className={`relative flex items-center gap-3 px-3 py-2 rounded-xl transition-colors ${
          active
            ? 'text-primary dark:text-secondary'
            : 'text-subtle-light dark:text-subtle-dark hover:bg-primary/5 hover:text-text-light dark:hover:text-text-dark'
        }`}
      >
        {/* Left accent bar when active */}
        {active && (
          <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r bg-primary dark:bg-secondary" />
        )}
        {/* Rounded-square icon housing — filled when active */}
        <span
          className={`flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-colors ${
            active
              ? 'bg-primary dark:bg-secondary'
              : 'bg-transparent'
          }`}
        >
          <span
            className={`material-icons text-[18px] ${
              active
                ? 'text-white dark:text-neutral'
                : 'text-subtle-light dark:text-subtle-dark'
            }`}
          >
            {item.icon}
          </span>
        </span>
        <span className="text-sm font-medium">{item.label}</span>
      </Link>
    )
  }

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark">
      {/* Backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar — surface_dim background, glass effect */}
      <aside
        id="sidebar"
        data-qtz-part="sidebar"
        className={`fixed left-0 top-0 h-full w-72 bg-surface-dim-light dark:bg-surface-dim-dark shadow-float dark:shadow-none dark:border-r dark:border-border-dark transform transition-transform duration-300 z-50 flex flex-col ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Header — logo + close */}
        <div className="flex items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3">
            <img src="/favicon.svg" alt="Quantizant" className="w-8 h-8 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-text-light dark:text-text-dark tracking-display leading-tight">
                Quantizant
              </p>
              <p className="text-[11px] label-sm text-subtle-light dark:text-subtle-dark">
                Discovery
              </p>
            </div>
          </div>
          <button
            id="closeSidebar"
            onClick={() => setIsSidebarOpen(false)}
            className="p-1.5 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark hover:bg-primary/5 transition-colors"
          >
            <span className="material-icons text-xl">close</span>
          </button>
        </div>

        {/* Org + user context — tonal inlay (Level 2) */}
        <div className="mx-4 mb-3 p-3 rounded-xl bg-container-light dark:bg-container-dark space-y-2">
          <div>
            <p className="label-sm text-subtle-light dark:text-subtle-dark mb-0.5">Organisation</p>
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-text-light dark:text-text-dark truncate" title={currentOrgId}>
                {orgName}
              </p>
              {orgPlan && (
                <span className="shrink-0 text-[10px] label-sm px-1.5 py-0.5 rounded-md bg-primary/10 text-primary">
                  {orgPlan}
                </span>
              )}
            </div>
          </div>
          {userEmail && (
            <div className="flex items-center gap-2 pt-1">
              <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0">
                <span className="text-[10px] font-bold text-white">{userInitials}</span>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-text-light dark:text-text-dark truncate">{userEmail}</p>
                {userRole && (
                  <p className="text-[11px] text-subtle-light dark:text-subtle-dark capitalize">{userRole}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="px-3 flex-1 overflow-y-auto">
          <p className="label-sm text-subtle-light dark:text-subtle-dark px-2 mb-2">Navigation</p>
          <ul className="space-y-0.5">
            {visibleNavItems.map((item) => (
              <li key={item.path} data-qtz-part="nav" data-qtz-nav={item.path}>
                <NavLink item={item} onClick={() => setIsSidebarOpen(false)} />
              </li>
            ))}
          </ul>

          {/* Separator — padding-only, no line */}
          <div className="h-4" />

          <ul className="space-y-0.5">
            {isRouteAllowed('/settings', allowedRoutes) && (
              <li data-qtz-part="nav" data-qtz-nav="/settings">
                <NavLink
                  item={{ path: '/settings', icon: 'settings', label: 'Settings' }}
                  onClick={() => setIsSidebarOpen(false)}
                />
              </li>
            )}
          </ul>
        </nav>

        {/* Footer */}
        <div className="px-6 py-4">
          <p className="text-[11px] label-sm text-subtle-light dark:text-subtle-dark">
            Quantizant PQC Platform · v0.1.0
          </p>
        </div>
      </aside>

      {/* Main content */}
      <div className="min-h-screen flex flex-col">
        {/* Sticky header — glass effect */}
        <header data-qtz-part="header" className="glass sticky top-0 z-30 px-6 py-3 dark:border-b dark:border-border-dark">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                id="menuToggle"
                data-qtz-part="menu-toggle"
                onClick={() => setIsSidebarOpen(true)}
                className="p-1.5 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark hover:bg-primary/5 transition-colors"
                aria-label="Open menu"
              >
                <span className="material-icons">menu</span>
              </button>
              <div className="flex items-center gap-2.5">
                <img data-qtz-part="brand" src="/favicon.svg" alt="Quantizant" className="w-7 h-7" />
                <div>
                  <h1 data-qtz-part="title" className="text-base font-semibold text-text-light dark:text-text-dark tracking-display leading-tight">
                    Quantizant Discovery
                  </h1>
                  <Breadcrumb />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Org chip — tonal, no border */}
              <span data-qtz-part="org-badge" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark max-w-[200px]">
                <span className="material-icons text-[14px] shrink-0">corporate_fare</span>
                <span className="truncate font-medium" title={currentOrgId}>{orgName}</span>
              </span>

              {/* Theme toggle */}
              <button
                data-qtz-part="theme-toggle"
                onClick={toggleTheme}
                className="p-2 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark hover:bg-primary/5 transition-colors"
                aria-label="Toggle theme"
              >
                <span className="material-icons text-xl">
                  {theme === 'light' ? 'dark_mode' : 'light_mode'}
                </span>
              </button>

              {/* User avatar */}
              <div data-qtz-part="user-menu" className="relative">
                <button
                  onClick={() => setShowUserMenu((v) => !v)}
                  className="w-8 h-8 bg-primary rounded-full flex items-center justify-center hover:opacity-90 transition-opacity"
                  aria-label="User menu"
                >
                  {userInitials !== '?' ? (
                    <span className="text-xs font-bold text-white">{userInitials}</span>
                  ) : (
                    <span className="material-icons text-white text-base">person</span>
                  )}
                </button>

                {showUserMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                    <div className="absolute right-0 top-10 z-50 w-64 bg-surface-light dark:bg-surface-dark rounded-2xl shadow-float dark:shadow-none dark:border dark:border-border-dark p-4 space-y-3">
                      {/* User info */}
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0">
                          {userInitials !== '?' ? (
                            <span className="text-sm font-bold text-white">{userInitials}</span>
                          ) : (
                            <span className="material-icons text-white">person</span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-text-light dark:text-text-dark truncate">
                            {userEmail || 'Unknown user'}
                          </p>
                          {userRole && (
                            <p className="text-xs text-subtle-light dark:text-subtle-dark capitalize">{userRole}</p>
                          )}
                        </div>
                      </div>

                      {/* Org info — inlay well */}
                      <div className="p-3 rounded-xl bg-container-light dark:bg-container-dark">
                        <p className="label-sm text-subtle-light dark:text-subtle-dark mb-1">Organisation</p>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-text-light dark:text-text-dark truncate">{orgName}</p>
                          {orgPlan && (
                            <span className="shrink-0 text-[10px] label-sm px-1.5 py-0.5 rounded-md bg-primary/10 text-primary">
                              {orgPlan}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-subtle-light dark:text-subtle-dark font-mono mt-1 truncate" title={currentOrgId}>
                          {currentOrgId}
                        </p>
                      </div>

                      {/* Logout */}
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-error hover:bg-error/5 rounded-xl transition-colors"
                      >
                        <span className="material-icons text-base">logout</span>
                        Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main data-qtz-part="main" className="flex-grow p-6">
          {routeAllowed ? (
            <Outlet />
          ) : location.pathname === '/' && allowedRoutes?.length ? (
            // Dashboard not allowed — land on the first allowed route instead.
            <Navigate to={allowedRoutes[0]} replace />
          ) : (
            <div id="route-not-allowed" className="flex flex-col items-center justify-center py-24 gap-2 text-subtle-light dark:text-subtle-dark">
              <span className="material-icons text-4xl">block</span>
              <p className="text-sm">Not available in this view.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}
